import * as a11y from '../vendor/theme-scripts/theme-a11y';
import debounce from '../util/debounce';
import scrollTo from '../util/scroll-to';
import {isMobile} from '../util/media-query';

const selectors = {
  section: '[data-section-type]',
  collectionSidebar: '[data-collection-sidebar]',
  collectionSidebarSlideOut: '[data-collection-sidebar-slide-out]',
  form: '[data-collection-filters-form]',
  input: 'input',
  select: 'select',
  label: 'label',
  textarea: 'textarea',
  priceMin: '[data-field-price-min]',
  priceMax: '[data-field-price-max]',
  priceMinValue: 'data-field-price-min',
  priceMaxValue: 'data-field-price-max',
  rangeMin: '[data-se-min-value]',
  rangeMax: '[data-se-max-value]',
  rangeMinValue: 'data-se-min-value',
  rangeMaxValue: 'data-se-max-value',
  rangeMinDefault: 'data-se-min',
  rangeMaxDefault: 'data-se-max',
  productsContainer: '[data-products-grid]',
  product: '[data-product-grid-item]',
  filterUpdateUrlButton: '[data-filter-update-url]',
  activeFilters: '[data-active-filters]',
  activeFiltersCount: 'data-active-filters-count',
  sort: 'data-sort-enabled',
  collectionNav: '[data-collection-nav]',
  showMoreOptions: '[data-show-more]',
  linkHidden: '[data-link-hidden]',
};

const classes = {
  hidden: 'hidden',
  loading: 'is-loading',
  focused: 'is-focused',
};

class CollectionFiltersForm extends HTMLElement {
  constructor() {
    super();

    this.container = this.closest(selectors.section);
    this.sidebar = this.container.querySelector(selectors.collectionSidebar);
    this.collectionSidebarSlideOut = this.container.querySelector(selectors.collectionSidebarSlideOut);
    this.sort = this.container.querySelector(`[${selectors.sort}]`);
    this.productsContainer = this.container.querySelector(selectors.productsContainer);
    this.collectionNav = this.container.querySelector(selectors.collectionNav);
    this.form = this.querySelector(selectors.form);
    this.showMoreOptions = this.querySelectorAll(selectors.showMoreOptions);
    this.accessibility = a11y;

    this.filterUpdateFromUrlEvent = (e) => this.filterUpdateFromUrl(e);
    this.submitFormEvent = (e) => this.submitForm(e);
    this.updatePriceEvent = debounce((e) => this.updatePrice(e), 500);
    this.updateRangeEvent = (e) => this.updateRange(e);
    this.showMoreEvent = (e) => this.showMore(e);
  }

  connectedCallback() {
    if (this.form) {
      this.sidebar.addEventListener('input', this.updatePriceEvent);

      this.sidebar.addEventListener('theme:range:update', this.updateRangeEvent);
    }

    if (this.sidebar) {
      this.sidebar.addEventListener('click', this.filterUpdateFromUrlEvent);
    }

    if (this.productsContainer) {
      this.productsContainer.addEventListener('click', this.filterUpdateFromUrlEvent);
    }

    if (this.sort) {
      this.container.addEventListener('theme:filter:sort-update', this.submitFormEvent);
    }

    if (this.sidebar || this.sort) {
      window.addEventListener('popstate', this.submitFormEvent);
    }

    if (this.showMoreOptions.length) {
      // Show more options from the group
      this.showMoreOptions.forEach((element) => {
        element.addEventListener('click', this.showMoreEvent);
      });
    }
  }

  showMore(e) {
    e.preventDefault();

    e.target.parentElement.classList.add(classes.hidden);

    e.target.parentElement.previousElementSibling.querySelectorAll(selectors.linkHidden).forEach((link, index) => {
      link.classList.remove(classes.hidden);
      const input = link.querySelector(selectors.input);
      if (index === 0 && document.body.classList.contains(classes.focused) && input) {
        if (this.collectionSidebarSlideOut || isMobile()) {
          this.accessibility.removeTrapFocus();
          this.accessibility.trapFocus(this.sidebar, {
            elementToFocus: input,
          });
        } else {
          input.focus();
        }
      }
    });
  }

  updatePrice(e) {
    const type = e.type;
    const target = e.target;

    if (type === selectors.input || type === selectors.select || type === selectors.label || type === selectors.textarea) {
      if (this.form && typeof this.form.submit === 'function') {
        const priceMin = this.form.querySelector(selectors.priceMin);
        const priceMax = this.form.querySelector(selectors.priceMax);
        if (priceMin && priceMax) {
          if (target.hasAttribute(selectors.priceMinValue) && !priceMax.value) {
            priceMax.value = priceMax.placeholder;
          } else if (target.hasAttribute(selectors.priceMaxValue) && !priceMin.value) {
            priceMin.value = priceMin.placeholder;
          }
        }

        this.submitForm(e);
      }
    }
  }

  filterUpdateFromUrl(e) {
    const target = e.target;
    if (target.matches(selectors.filterUpdateUrlButton) || (target.closest(selectors.filterUpdateUrlButton) && target)) {
      e.preventDefault();
      const button = target.matches(selectors.filterUpdateUrlButton) ? target : target.closest(selectors.filterUpdateUrlButton);
      this.submitForm(e, button.getAttribute('href'));
    }
  }

  submitForm(e, replaceHref = '') {
    this.sort = this.container.querySelector(`[${selectors.sort}]`);
    const sortValue = this.sort ? this.sort.getAttribute(selectors.sort) : '';
    if (!e || (e && e.type !== 'popstate')) {
      if (replaceHref === '') {
        const url = new window.URL(window.location.href);
        let filterUrl = url.searchParams;
        const filterUrlEntries = filterUrl;
        const filterUrlParams = Object.fromEntries(filterUrlEntries);
        const filterUrlRemoveString = filterUrl.toString();

        if (filterUrlRemoveString.includes('filter.') || filterUrlRemoveString.includes('page=')) {
          for (const key in filterUrlParams) {
            if (key.includes('filter.') || key === 'page') {
              filterUrl.delete(key);
            }
          }
        }

        if (this.form) {
          const formData = new FormData(this.form);
          const formParams = new URLSearchParams(formData);
          const rangeMin = this.form.querySelector(selectors.rangeMin);
          const rangeMax = this.form.querySelector(selectors.rangeMax);
          const rangeMinDefaultValue = rangeMin && rangeMin.hasAttribute(selectors.rangeMinDefault) ? rangeMin.getAttribute(selectors.rangeMinDefault) : '';
          const rangeMaxDefaultValue = rangeMax && rangeMax.hasAttribute(selectors.rangeMaxDefault) ? rangeMax.getAttribute(selectors.rangeMaxDefault) : '';
          let priceFilterDefaultCounter = 0;

          for (let [key, val] of formParams.entries()) {
            if (key.includes('filter.') && val) {
              filterUrl.append(key, val);

              if ((val === rangeMinDefaultValue && key === 'filter.v.price.gte') || (val === rangeMaxDefaultValue && key === 'filter.v.price.lte')) {
                priceFilterDefaultCounter += 1;
              }
            }
          }

          if (priceFilterDefaultCounter === 2) {
            filterUrl.delete('filter.v.price.gte');
            filterUrl.delete('filter.v.price.lte');
          }
        }

        if (sortValue || (e && e.detail && e.detail.href)) {
          const sortString = sortValue ? sortValue : e.detail.href;
          filterUrl.set('sort_by', sortString);
        }

        const filterUrlString = filterUrl.toString();
        const filterNewParams = filterUrlString ? `?${filterUrlString}` : location.pathname;
        window.history.pushState(null, '', filterNewParams);
      } else {
        window.history.pushState(null, '', replaceHref);
      }
    } else if (this.sort) {
      this.sort.dispatchEvent(new CustomEvent('theme:filter:sort', {bubbles: false}));
    }

    if (this.productsContainer) {
      this.productsContainer.classList.add(classes.loading);
      fetch(`${window.location.pathname}${window.location.search}`)
        .then((response) => response.text())
        .then((data) => {
          this.productsContainer.innerHTML = new DOMParser().parseFromString(data, 'text/html').querySelector(selectors.productsContainer).innerHTML;

          if (this.sidebar) {
            this.sidebar.innerHTML = new DOMParser().parseFromString(data, 'text/html').querySelector(selectors.collectionSidebar).innerHTML;

            const activeFiltersCountContainer = this.sidebar.querySelector(`[${selectors.activeFiltersCount}]`);
            const activeFiltersContainer = this.container.querySelectorAll(selectors.activeFilters);
            if (activeFiltersCountContainer && activeFiltersContainer.length) {
              const activeFiltersCount = parseInt(activeFiltersCountContainer.getAttribute(selectors.activeFiltersCount));

              activeFiltersContainer.forEach((counter) => {
                counter.textContent = activeFiltersCount;
                counter.classList.toggle(classes.hidden, activeFiltersCount < 1);
              });
            }

            this.sidebar.dispatchEvent(new CustomEvent('theme:filter:update', {bubbles: true}));
          }

          if (this.collectionNav) {
            scrollTo(this.productsContainer.getBoundingClientRect().top - this.collectionNav.offsetHeight);
          }

          setTimeout(() => {
            this.productsContainer.classList.remove(classes.loading);
          }, 500);
        })
        .catch((error) => {
          console.log(error);
        });
    }
  }

  updateRange(e) {
    if (this.form && typeof this.form.submit === 'function') {
      const rangeMin = this.form.querySelector(selectors.rangeMin);
      const rangeMax = this.form.querySelector(selectors.rangeMax);
      const priceMin = this.form.querySelector(selectors.priceMin);
      const priceMax = this.form.querySelector(selectors.priceMax);
      const checkElements = rangeMin && rangeMax && priceMin && priceMax;

      if (checkElements && rangeMin.hasAttribute(selectors.rangeMinValue) && rangeMax.hasAttribute(selectors.rangeMaxValue)) {
        const priceMinValue = parseInt(priceMin.placeholder);
        const priceMaxValue = parseInt(priceMax.placeholder);
        const rangeMinValue = parseInt(rangeMin.getAttribute(selectors.rangeMinValue));
        const rangeMaxValue = parseInt(rangeMax.getAttribute(selectors.rangeMaxValue));

        if (priceMinValue !== rangeMinValue || priceMaxValue !== rangeMaxValue) {
          priceMin.value = rangeMinValue;
          priceMax.value = rangeMaxValue;

          this.submitForm(e);
        }
      }
    }
  }

  disconnectedCallback() {
    if (this.form) {
      this.sidebar.removeEventListener('input', this.updatePriceEvent);

      this.sidebar.removeEventListener('theme:range:update', this.updateRangeEvent);
    }

    if (this.sidebar) {
      this.sidebar.removeEventListener('click', this.filterUpdateFromUrlEvent);
    }

    if (this.productsContainer) {
      this.productsContainer.removeEventListener('click', this.filterUpdateFromUrlEvent);
    }

    if (this.sort) {
      this.container.removeEventListener('theme:filter:sort-update', this.submitFormEvent);
    }

    if (this.sidebar || this.sort) {
      window.removeEventListener('popstate', this.submitFormEvent);
    }

    if (this.showMoreOptions.length) {
      this.showMoreOptions.forEach((element) => {
        element.removeEventListener('click', this.showMoreEvent);
      });
    }
  }
}

export {CollectionFiltersForm};
